import React, { useState, useEffect, useCallback } from 'react';
import { inquiriesApi, formatApiErrorDetail } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { MessageSquare, Loader2, Phone, Building2, User, Eye, Mail } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  NEW: 'bg-blue-50 text-blue-700 border-blue-200',
  CONTACTED: 'bg-amber-50 text-amber-700 border-amber-200',
  CONVERTED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CLOSED: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function InquiriesPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data: res } = await inquiriesApi.list(params);
      setData(res);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await inquiriesApi.update(id, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="inquiries-heading">Inquiries</h1>
            <p className="text-slate-600 mt-1">Leads from the landing page contact form</p>
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="inquiry-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="NEW">New</SelectItem>
              <SelectItem value="CONTACTED">Contacted</SelectItem>
              <SelectItem value="CONVERTED">Converted</SelectItem>
              <SelectItem value="CLOSED">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : data.items.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map(inq => (
                      <TableRow key={inq.id} className="table-row-hover">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-slate-900">{inq.name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                            <span>{inq.phone || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3.5 w-3.5 text-slate-400" />
                            <span>{inq.company_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">{formatDateTime(inq.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[inq.status] || STATUS_COLORS.NEW} border`} data-testid={`inquiry-status-${inq.id}`}>
                            {inq.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedInquiry(inq); setShowViewDialog(true); }}
                              data-testid={`inquiry-view-${inq.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Select value={inq.status} onValueChange={(v) => handleStatusChange(inq.id, v)}>
                              <SelectTrigger className="w-32 h-8 text-xs" data-testid={`inquiry-action-${inq.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="NEW">New</SelectItem>
                                <SelectItem value="CONTACTED">Contacted</SelectItem>
                                <SelectItem value="CONVERTED">Converted</SelectItem>
                                <SelectItem value="CLOSED">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination page={data.page} pages={data.pages} total={data.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No inquiries yet</h3>
                <p className="text-slate-500 mt-1">Leads from the landing page will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Inquiry Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Inquiry Details</DialogTitle>
            </DialogHeader>
            {selectedInquiry && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Name</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      <p className="font-medium text-slate-900">{selectedInquiry.name || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    <Badge className={`${STATUS_COLORS[selectedInquiry.status] || STATUS_COLORS.NEW} border`}>
                      {selectedInquiry.status}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <p className="text-slate-900">{selectedInquiry.phone || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Email</p>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <p className="text-slate-900">{selectedInquiry.email || '-'}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Company</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <p className="text-slate-900">{selectedInquiry.company_name || '-'}</p>
                  </div>
                </div>
                {selectedInquiry.gstin && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">GSTIN</p>
                    <p className="text-slate-900">{selectedInquiry.gstin}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 mb-1">Submitted</p>
                  <p className="text-slate-900">{formatDateTime(selectedInquiry.created_at)}</p>
                </div>
                {selectedInquiry.notes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-slate-700 bg-slate-50 p-3 rounded-lg text-sm">{selectedInquiry.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
